import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface QRCampaign {
  id: string;
  name: string;
  description: string | null;
  utm_campaign: string;
  utm_content: string | null;
  cost_of_print: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  points?: QRPoint[];
}

export interface QRPoint {
  id: string;
  campaign_id: string;
  name: string;
  utm_content: string | null;
  scans: number;
  created_at: string;
}

export interface QRScan {
  id: string;
  campaign_id: string;
  point_id: string | null;
  scanned_at: string;
}

export function useQRCampaigns() {
  return useQuery({
    queryKey: ["qr-campaigns"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("qr_campaigns")
        .select("*, qr_points(*)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as QRCampaign[];
    },
  });
}

export function useCreateQRCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; description?: string; utm_campaign: string; utm_content?: string; cost_of_print?: number }) => {
      const { data, error } = await supabase.from("qr_campaigns").insert(input).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["qr-campaigns"] }),
  });
}

export function useUpdateQRCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<QRCampaign> & { id: string }) => {
      const { error } = await supabase.from("qr_campaigns").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["qr-campaigns"] }),
  });
}

export function useDeleteQRCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("qr_campaigns").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["qr-campaigns"] }),
  });
}

export function useCreateQRPoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { campaign_id: string; name: string; utm_content?: string }) => {
      const { data, error } = await supabase.from("qr_points").insert(input).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["qr-campaigns"] }),
  });
}

export function useDeleteQRPoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("qr_points").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["qr-campaigns"] }),
  });
}

// Stats for a campaign: derive from telegram_users with matching utm
export function useQRCampaignStats(utmCampaign: string | null, dateFrom?: string, dateTo?: string) {
  return useQuery({
    queryKey: ["qr-campaign-stats", utmCampaign, dateFrom, dateTo],
    enabled: !!utmCampaign,
    queryFn: async () => {
      if (!utmCampaign) return null;

      // Get QR scan count
      const { data: campaign } = await supabase
        .from("qr_campaigns")
        .select("id")
        .eq("utm_campaign", utmCampaign)
        .single();

      let scans = 0;
      if (campaign) {
        let scanQuery = supabase
          .from("qr_scans")
          .select("id", { count: "exact", head: true })
          .eq("campaign_id", campaign.id);
        if (dateFrom) scanQuery = scanQuery.gte("scanned_at", dateFrom);
        if (dateTo) scanQuery = scanQuery.lte("scanned_at", dateTo + "T23:59:59.999Z");
        const { count } = await scanQuery;
        scans = count || 0;
      }

      // Get users from this QR campaign
      let usersQuery = supabase
        .from("telegram_users")
        .select("id, utm_source, utm_campaign, utm_content, total_generations, created_at")
        .eq("utm_source", "qr")
        .eq("utm_campaign", utmCampaign);
      if (dateFrom) usersQuery = usersQuery.gte("created_at", dateFrom);
      if (dateTo) usersQuery = usersQuery.lte("created_at", dateTo + "T23:59:59.999Z");
      const { data: users, error: usersErr } = await usersQuery;
      if (usersErr) throw usersErr;

      const userIds = (users || []).map(u => u.id);
      if (userIds.length === 0) {
        return { scans, users: 0, startedCreation: 0, gotDemo: 0, paid: 0, revenue: 0, avgCheck: 0, conversion: 0, byContent: [] as any[] };
      }

      // Get generations
      const { data: gens } = await supabase
        .from("generations")
        .select("id, user_id, status, is_full_purchased, full_price_rub, demo_url")
        .in("user_id", userIds);

      // Get transactions
      const { data: txns } = await supabase
        .from("transactions")
        .select("id, user_id, price_rub, status")
        .in("user_id", userIds)
        .eq("status", "completed");

      const startedCreation = new Set((gens || []).map(g => g.user_id)).size;
      const withDemo = (gens || []).filter(g => g.demo_url || g.status === "completed");
      const gotDemo = new Set(withDemo.map(g => g.user_id)).size;
      const paidUserIds = new Set((txns || []).map(t => t.user_id));
      const paid = paidUserIds.size;
      const revenue = (txns || []).reduce((s, t) => s + t.price_rub, 0);
      const avgCheck = paid > 0 ? Math.round(revenue / (txns || []).length) : 0;
      const conversion = gotDemo > 0 ? Math.round((paid / gotDemo) * 100) : 0;

      // By utm_content (point)
      const contentMap: Record<string, { users: number; paid: number; revenue: number }> = {};
      (users || []).forEach(u => {
        const key = u.utm_content || "(без точки)";
        if (!contentMap[key]) contentMap[key] = { users: 0, paid: 0, revenue: 0 };
        contentMap[key].users++;
        if (paidUserIds.has(u.id)) contentMap[key].paid++;
      });
      (txns || []).forEach(t => {
        const user = (users || []).find(u => u.id === t.user_id);
        const key = user?.utm_content || "(без точки)";
        if (contentMap[key]) contentMap[key].revenue += t.price_rub;
      });

      const byContent = Object.entries(contentMap).map(([content, d]) => ({
        content,
        ...d,
        conversion: d.users > 0 ? Math.round((d.paid / d.users) * 100) : 0,
      }));

      return {
        scans,
        users: userIds.length,
        startedCreation,
        gotDemo,
        paid,
        revenue,
        avgCheck,
        conversion,
        byContent,
      };
    },
  });
}
